import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Page } from "@/components/Layout";
import Seo from "@/components/Seo";
import { api } from "@/lib/api";
import { useCart } from "@/lib/useCart";
import { useCurrency } from "@/lib/currency";
import { toast } from "sonner";
import { ShoppingCart, Lightning, CaretLeft, DownloadSimple, ShieldCheck, EnvelopeSimple, Star } from "@phosphor-icons/react";
import PageLoader from "@/components/PageLoader";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [qty, setQty] = useState(1);
  const [busy, setBusy] = useState(false);
  const { add: addCart } = useCart();
  const { format } = useCurrency();

  useEffect(() => {
    setBusy(true);
    api.get(`/products/${id}`)
      .then((r) => setProduct(r.data))
      .catch(() => toast.error("Product not found"))
      .finally(() => setBusy(false));
    api.get("/products").then((r) => setRelated((r.data || []).slice(0, 4)));
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [id]);

  if (busy || !product) {
    return <Page><PageLoader /></Page>;
  }

  const addNTimes = (n) => {
    for (let i = 0; i < n; i++) addCart(product);
  };

  const handleAdd = () => {
    addNTimes(qty);
    toast.success(`${product.name} × ${qty} added to cart`);
  };

  const handleBuyNow = () => {
    addNTimes(qty);
    navigate("/checkout");
  };

  return (
    <Page>
      <Seo title={product.name} description={product.description?.slice(0, 160)} />
      <section className="md-section pt-8 md:pt-12">
        <div className="md-container">
          <button onClick={() => navigate(-1)} className="pd-back" data-testid="pd-back">
            <CaretLeft size={14} weight="bold" /> Back
          </button>

          <div className="pd-grid mt-6">
            <div className="pd-gallery" data-testid="pd-gallery">
              {product.image_url ? (
                <img src={product.image_url} alt={product.name} className="pd-image" />
              ) : (
                <div className="pd-image-placeholder">No image</div>
              )}
            </div>

            <div className="pd-info">
              <div className="md-label-eyebrow mb-3" data-testid="pd-category">{product.category}</div>
              <h1 className="text-3xl md:text-4xl font-serif" data-testid="pd-name">{product.name}</h1>
              <div className="flex items-center gap-1 mt-3" data-testid="pd-rating">
                {[1, 2, 3, 4, 5].map((i) => <Star key={i} size={14} weight="fill" style={{ color: "var(--md-accent)" }} />)}
                <span className="text-xs ml-2" style={{ color: "var(--md-text-muted)" }}>4.8 · 124 reviews</span>
              </div>

              <div className="pd-price mt-5" data-testid="pd-price">{product.price > 0 ? format(product.price) : "Free"}</div>
              <p className="text-xs" style={{ color: "var(--md-text-muted)" }}>Inclusive of all taxes</p>

              <p className="pd-description mt-5" data-testid="pd-description">{product.description}</p>

              <div className="pd-qty-row mt-6">
                <span className="md-label-eyebrow">Quantity</span>
                <div className="pd-qty">
                  <button type="button" onClick={() => setQty(Math.max(1, qty - 1))} aria-label="Decrease" data-testid="pd-qty-dec">−</button>
                  <span data-testid="pd-qty-value">{qty}</span>
                  <button type="button" onClick={() => setQty(qty + 1)} aria-label="Increase" data-testid="pd-qty-inc">+</button>
                </div>
                {product.stock !== undefined && (
                  <span className="text-xs" style={{ color: product.stock > 0 ? "var(--md-secondary-dark)" : "#B33A3A" }}>
                    {product.stock > 0 ? `${product.stock} in stock` : "Out of stock"}
                  </span>
                )}
              </div>

              <div className="pd-cta-row mt-6">
                <button onClick={handleAdd} className="btn-outline pd-cta" data-testid="pd-add-cart">
                  <ShoppingCart size={16} weight="bold" /> Add to Cart
                </button>
                <button onClick={handleBuyNow} className="btn-primary pd-cta" data-testid="pd-buy-now">
                  <Lightning size={16} weight="bold" /> Buy Now
                </button>
              </div>

              <ul className="pd-trust mt-7" data-testid="pd-trust">
                <li><DownloadSimple size={16} weight="duotone" /> Instant digital delivery</li>
                <li><ShieldCheck size={16} weight="duotone" /> Secure Razorpay checkout</li>
                <li><EnvelopeSimple size={16} weight="duotone" /> Download link emailed instantly</li>
              </ul>
            </div>
          </div>

          {related.filter((r) => r.id !== product.id).length > 0 && (
            <div className="mt-16">
              <h2 className="text-2xl font-serif mb-5">You might also like</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="pd-related">
                {related.filter((r) => r.id !== product.id).slice(0, 4).map((p) => (
                  <Link key={p.id} to={`/shop/${p.id}`} className="md-card md-card-hover" data-testid={`pd-related-${p.id}`}>
                    {p.image_url && <img src={p.image_url} alt={p.name} className="w-full aspect-square object-cover rounded-lg mb-3" />}
                    <h3 className="font-serif text-base truncate">{p.name}</h3>
                    <p className="text-sm mt-1 font-medium" style={{ color: "var(--md-primary)" }}>{p.price > 0 ? format(p.price) : "Free"}</p>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </Page>
  );
}
